import { ContactHistorySearchListService } from './../../core/service/grid-data/grid-data-contact-history.service';
import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { ContactHistoryService } from 'src/app/core/service/contact-history.service';
import { NGXLogger } from 'ngx-logger';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { IntlService } from '@progress/kendo-angular-intl';
import { GridDataResult, DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { State } from '@progress/kendo-data-query';
import { ContactHistoryData } from '../model/contact-history-details';
import { Subscription } from 'rxjs';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  standalone: false,
  selector: 'app-contact-history-section',
  templateUrl: './contact-history-section.component.html',
  styleUrls: ['./contact-history-section.component.scss']
})
export class ContactHistorySectionComponent implements OnInit, OnDestroy
{
  @Input() public memberId: number;

  @Output() public refresh: EventEmitter<any> = new EventEmitter();

  public pageSizes = [5, 10, 25, 50, 100];
  public showActivities = false;
  public showConsent = false;
  public showEntity = false;
  public showIntensity = false;
  public showMethod = false;
  public showMood = false;
  /**
   * Contact History of this Member.
   */
  public contactHistory: any[] = [];

  /**
   * Contact history selected
   */
  public contact: any;

  public visibleInitiativeColumn = false;
  public visibleAreaColumn = false;
  public contactHistoryDeleteDialogOpened = false;
  public contactHistoryDialogOpened = false;
  public contactHistoryView = new GridViewManager();
  public contactedOn: string;
  public contactMembers = [];
  public gridView: GridDataResult;
  public gridData: any = {};
  public total: number;
  public count: number;
  public enableAdvanced = false;
  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    }
  };

  private listSubscription: Subscription;

  constructor(
    private contactHistoryService: ContactHistoryService,
    private contactHistorySearchListService: ContactHistorySearchListService,
    private logger: NGXLogger,
    private messageHandlerService: MessageHandlerService,
    private settingService: SettingService,
    private appConfigService: AppConfigService,
    public intl: IntlService,
    private authenticationService: AuthenticationService,
    private translateService: TranslateService,
  ) { }

  /**
   * Provide loading property from Service.
   */
  public get loading(): boolean
  {
    return this.contactHistorySearchListService ? this.contactHistorySearchListService.loading : false;
  }

  public ngOnInit(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {        
        this.showActivities = sessionSetting.recordContactFields.includes(this.translateService.instant('ACTIVITIES').toUpperCase()) ? true : false;
        this.showConsent = sessionSetting.recordContactFields.includes(this.translateService.instant('CONSENT').toUpperCase()) ? true : false;
        this.showEntity = sessionSetting.recordContactFields.includes(this.translateService.instant('ENTITY').toUpperCase()) ? true : false;
        this.showIntensity = sessionSetting.recordContactFields.includes(this.translateService.instant('INTENSITY').toUpperCase()) ? true : false;
        this.showMethod = sessionSetting.recordContactFields.includes(this.translateService.instant('METHOD').toUpperCase()) ? true : false;
        this.showMood = sessionSetting.recordContactFields.includes(this.translateService.instant('SENTIMENT').toUpperCase()) ? true : false;
      }
    });

    this.listSubscription = this.contactHistorySearchListService.subscribe({
      next: (value: ContactHistoryData) =>
      {
        // Preprocessing data before it's passed to the Kendo Grid.
        if (value !== null)
        {
          value.data.forEach((d: any) =>
          {
            d.contactedOn = new Date(d.contactedOn);
            if (d.comments === null)
            {
              d.comments = '';
            }
            d.methodName = d.method.displayName;
            d.entityName = d.entity ? d.entity.displayName : '';
            d.consentName = d.consent ? d.consent.displayName : '';
            d.intensityName = d.intensity ? d.intensity.displayName : '';
            d.initiativeName = d.initiative ? d.initiative.displayName : '';
            // Make initiative column visible if data is loaded.
            if (d.initiative)
            {
              this.visibleInitiativeColumn = true;
            }
            // Make focus areas column visible if data is loaded.
            if (d.areas && d.areas.length > 0)
            {
              this.visibleAreaColumn = true;
            }
          });
        }
        this.gridView = {
          data: (value) ? this.gridData = value.data : this.gridData = [],
          total: (value) ? this.total = value.total : this.total = 0
        };
      }
    });
    this.getContactMembers();
  }

  public ngOnDestroy(): void
  {
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public contactHistoryDataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.contactHistorySearchListService.queryByState(state);
  }

  public onContactHistoryAdded(): void
  {
    // for now, just fetching the dataset again.
    this.getContactMembers();
    this.contactHistoryDialogOpened = false;
    this.refresh.emit();
  }

  public moodLabelForValue = (e: any): string =>
  {
    switch (e.value)
    {
      case 0: case 1: case 2: case 3: case 4:
        {
          return this.contactHistoryService.patientMoods[e.value].text;
        }
      default:
        {
          return '';
        }
    }
  };

  public openEditContact(contactSelected: any): void
  {
    this.contactHistoryService.getContactHistory(contactSelected.id).subscribe({
      next: (contactHistory: any) =>
      {
        contactHistory.contactedOn = new Date(contactHistory.contactedOn);
        this.contact = contactHistory;
        this.contactHistoryDialogOpened = true;
      },
      error: (error) =>
      {
        this.messageHandlerService.displayDirectMessage('error', error.message);
      }
    });
  }

  public openDeleteContact(contactSelected: any): void
  {
    this.contact = contactSelected;
    this.contactHistoryDeleteDialogOpened = true;
    this.contactedOn = new Date(this.contact.contactedOn).toLocaleDateString() + ' '
      + new Date(this.contact.contactedOn).toLocaleTimeString();
  }

  public deleteContactHistory(): void
  {
    this.contactHistoryService.deleteContactHistory(this.contact.id).subscribe({
      next: () =>
      {
        this.getContactMembers();
        this.contactHistoryDeleteDialogOpened = false;
        this.messageHandlerService.displaySuccess('CONTACT-HISTORY-DELETED-SUCCESFULLY');
        this.refresh.emit();
      },
      error: error =>
      {
        this.logger.error(error);
        this.messageHandlerService.displayDirectMessage('error', error.message);
      }
    });
  }

  public getMoodDisplayText(mood: string): string
  {
    return this.contactHistoryService.getMoodDisplayText(mood);
  }

  public getMoodClass(mood: string): string
  {
    return `mood-icon-${mood}`;
  }

  /**
   * Indicates if the grid details visibility. Details will show only if the message have been processed.
   *
   * @param dataItem Grid dataItem
   * @param index Index of the data in the grid list.
   */
  public showDetails(dataItem: any): boolean
  {
    return dataItem.comments;
  }

  private getContactMembers(): void
  {
    this.visibleInitiativeColumn = false;
    this.visibleAreaColumn = false;
    this.state = {
      filter: {
        filters: [
          { field: 'memberId', operator: 'eq', value: this.memberId },
          { field: 'deleted', operator: 'eq', value: false }
        ], logic: 'and'
      }, group: [], skip: 0,
      sort: [
        { field: 'contactedOn', dir: 'desc' }
      ],
      take: this.appConfigService.getConfig().pageSize,
    };
    this.contactHistorySearchListService.queryByState(this.state);
  }
}
